import {
  OktaAuthOptions,
  TokenManagerOptions,
  OktaAuth,
} from '@okta/okta-auth-js';
import { OnAuthRequiredFunction } from '@okta/okta-react/bundles/types/OktaContext';
import { OktaClientConfiguration } from '@ssar-app/okta-authn-core';

const buildKey = (firstInput: string, ...restOfInputs: string[]) => {
  return [firstInput].concat(restOfInputs || []).join('|');
};

const oktaAuthMap: Map<string, OktaAuth> = new Map();

const buildOktaAuth = (
  oktaClientConfiguration: OktaClientConfiguration,
  oktaAuthManagerConfig: OktaAuthManagerConfig,
) => {
  const devMode = !!oktaAuthManagerConfig?.devMode;
  const tokenManager: TokenManagerOptions = {
    storageKey: oktaAuthManagerConfig.storageKey(oktaClientConfiguration),
  };
  const commonConfig: OktaAuthOptions = {
    pkce: false,
    tokenManager,
    services: {
      autoRemove: true,
      autoRenew: true,
      syncStorage: true,
    },
    devMode,
    ...(oktaAuthManagerConfig.oktaAuthOptions || {}),
  };

  const { config } = oktaClientConfiguration;

  const newConfig: OktaAuthOptions = {
    ...commonConfig,
    ...config,
  };
  const oktaAuth = new OktaAuth(newConfig);
  return oktaAuth;
};

export type OktaAuthManager = {
  getForOktaClientCode(code: string): OktaAuth | undefined;
  readonly config: OktaAuthManagerConfig;
};

export type OktaAuthManagerConfig = {
  storageKey: (oktaClientConfiguration: OktaClientConfiguration) => string;
  onAuthRequired: OnAuthRequiredFunction;
  oktaAuthOptions: OktaAuthOptions & {
    restoreOriginalUri: (
      oktaAuth: OktaAuth,
      originalUri?: string,
    ) => Promise<void>;
  };
  devMode?: boolean;
};

const getOktaAuthBuilder =
  (config: OktaAuthManagerConfig) =>
  (oktaClientConfiguration: OktaClientConfiguration | null | undefined) => {
    if (!oktaClientConfiguration) {
      return undefined;
    }

    const key = buildKey(oktaClientConfiguration.code);
    if (!oktaAuthMap.has(key)) {
      const oktaAuth = buildOktaAuth(oktaClientConfiguration, config);
      oktaAuthMap.set(key, oktaAuth);
    }
    return oktaAuthMap.get(key);
  };

export const buildOktaAuthManager = (
  oktaClientConfigurations: OktaClientConfiguration[],
  config: OktaAuthManagerConfig,
): Readonly<OktaAuthManager> => {
  const getOktaAuth = getOktaAuthBuilder(config);
  return {
    config: config,
    getForOktaClientCode(oktaCode) {
      const oktaClientConfiguration = oktaClientConfigurations.find(
        (a) => a.code === oktaCode,
      );
      return getOktaAuth(oktaClientConfiguration);
    },
  };
};
